import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Target, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Section, Field, ErrorBox, inputCls, errMsg, fmtDate } from "./shared";

const FIELDS = [
  ["objective", "Objective", true],
  ["target_consumer", "Target consumer", false],
  ["requirements", "Requirements", true],
  ["packaging", "Packaging", false],
  ["target_cost", "Target cost", false],
  ["shelf_life_target", "Shelf-life target", false],
];

function toForm(p) {
  const f = {};
  FIELDS.forEach(([k]) => { f[k] = p?.[k] ?? ""; });
  f.targets = (p?.targets || []).map((t) => ({
    name: t.name || "", unit: t.unit || "",
    min: t.min ?? "", max: t.max ?? "", expected: t.expected || "",
  }));
  return f;
}

export default function BriefPanel({ recipeId, project, onSaved }) {
  const [form, setForm] = useState(() => toForm(project));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const loadedAt = useRef(project?.updated_at ?? null);

  // Only rehydrate from server when not editing, so errors never wipe input.
  useEffect(() => {
    if (!dirty) { setForm(toForm(project)); loadedAt.current = project?.updated_at ?? null; }
  }, [project, dirty]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const setT = (i, k, v) => { setForm((f) => ({ ...f, targets: f.targets.map((t, j) => (j === i ? { ...t, [k]: v } : t)) })); setDirty(true); };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const num = (v) => (v === "" || v == null || isNaN(Number(v)) ? null : Number(v));
      const body = {
        ...Object.fromEntries(FIELDS.map(([k]) => [k, form[k]])),
        targets: form.targets.filter((t) => t.name.trim()).map((t) => ({
          name: t.name.trim(), unit: t.unit, min: num(t.min), max: num(t.max), expected: t.expected,
        })),
        expected_updated_at: loadedAt.current ?? null,
      };
      await api.put(`/rd/recipes/${recipeId}/brief`, body);
      setDirty(false);
      toast.success("Brief saved");
      onSaved();
    } catch (err) {
      setError(`${errMsg(err)} Your edits are preserved below${err?.response?.status === 409 ? " — someone else changed this brief; reload to compare before saving again" : ""}.`);
    } finally { setSaving(false); }
  };

  return (
    <Section icon={Target} title="Product brief" testId="rd-brief"
      subtitle={`Targets defined here are used to assess analytical results server-side.${project?.updated_at ? ` Last saved ${fmtDate(project.updated_at)}.` : " Not saved yet."}`}
      actions={
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs font-semibold text-amber-700" data-testid="text-brief-unsaved">Unsaved changes</span>}
          {dirty && <Button size="sm" variant="ghost" onClick={() => { setDirty(false); }} data-testid="button-brief-discard">Discard</Button>}
          <Button size="sm" disabled={!dirty || saving} onClick={save} className="bg-[#17324D] hover:bg-[#22476B] text-white" data-testid="button-brief-save">
            <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save brief"}
          </Button>
        </div>
      }>
      <ErrorBox message={error} testId="error-brief" />
      <div className="grid sm:grid-cols-2 gap-3 mt-2">
        {FIELDS.map(([k, label, long]) => (
          <div key={k} className={long ? "sm:col-span-2" : ""}>
            <Field label={label}>
              {long ? (
                <textarea rows={3} className={inputCls} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`input-brief-${k}`} />
              ) : (
                <input className={inputCls} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`input-brief-${k}`} />
              )}
            </Field>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Measurable targets</div>
          <Button size="sm" variant="outline" onClick={() => { setForm((f) => ({ ...f, targets: [...f.targets, { name: "", unit: "", min: "", max: "", expected: "" }] })); setDirty(true); }} data-testid="button-add-target">
            <Plus size={13} className="mr-1" /> Add target
          </Button>
        </div>
        {form.targets.length === 0 ? (
          <p className="text-sm text-stone-500">No targets yet. Add measurable specs such as pH, water activity or Brix so test results can be assessed.</p>
        ) : (
          <div className="space-y-2">
            {form.targets.map((t, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr_2fr_auto] gap-2 items-center" data-testid={`row-target-${i}`}>
                <input className={inputCls} placeholder="Name (e.g. pH)" value={t.name} onChange={(e) => setT(i, "name", e.target.value)} />
                <input className={inputCls} placeholder="Unit" value={t.unit} onChange={(e) => setT(i, "unit", e.target.value)} />
                <input className={inputCls} type="number" step="any" placeholder="Min" value={t.min} onChange={(e) => setT(i, "min", e.target.value)} />
                <input className={inputCls} type="number" step="any" placeholder="Max" value={t.max} onChange={(e) => setT(i, "max", e.target.value)} />
                <input className={inputCls} placeholder="Expected (text)" value={t.expected} onChange={(e) => setT(i, "expected", e.target.value)} />
                <Button size="sm" variant="ghost" onClick={() => { setForm((f) => ({ ...f, targets: f.targets.filter((_, j) => j !== i) })); setDirty(true); }} data-testid={`button-remove-target-${i}`}>
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
